import { useLocation } from "@reach/router"
import classNames from "classnames"
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useLayoutEffect,
} from "react"

import { NavigationV2Context } from "./context"
import HamburgerIcon from "./HamburgerIcon"
import MobileNavPanels from "./MobileNavPanels"
import NavHeading, { MainLink } from "./NavHeading"
import { useGetCartQuantity } from "../../../cart/hooks"
import { CartContext } from "../../../cart/store"
import CartWidget from "../../../cart/V2/CartWidget"
import { Color } from "../../../constants/V2/color"
import { handleOnClick } from "../../../utils/buttonEventHandler"
import PillButton from "../../elements/V2/Buttons/PillButton"
import YocoLogoIcon from "../../elements/V2/Icons/YocoLogoIcon"
import Section from "../../elements/V2/Section"

import Link from "@components/elements/Link"
import { usePageLayoutState } from "@utils/layout"
import { useIsBreakpoint } from "@utils/V2/screen"

type Props = {
  mode?: "light" | "dark"
  hideMobileMenu?: boolean
  isBackgroundTransparent?: boolean
  mainLinks?: MainLink[]
  primaryButtonText?: string
  primaryButtonLink?: string
  secondaryButtonText: string
  secondaryButtonLink: string
  mobilePrimaryButtonText?: string
  mobilePrimaryButtonLink?: string
  isBannerContents?: boolean
  /**
   * Whether to show the cart widget
   *  @default true
   */
  showCartWidget?: boolean
  secondaryButtonTrackingEvent?: string
  secondaryButtonTrackingEventKey?: string
  secondaryButtonTrackingEventValue?: string
} & Partial<Storyblok.CTATrackingEvent>

export const MAIN_LINK_CLASSES =
  "flex lg:inline-flex justify-between items-center align-middle mr-8 lg:px-[10px] py-24 lg:pt-[11px] lg:pb-[9px] cursor-pointer bg-transparent lg:rounded-8 lg:hover:bg-white lg:focus-visible:bg-white lg:hover:text-black lg:focus-visible:text-black transition-all duration-200 ease-in-out"

const Navigation = ({
  mode = "light",
  hideMobileMenu = false,
  isBackgroundTransparent = true,
  mainLinks,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
  mobilePrimaryButtonText,
  mobilePrimaryButtonLink,
  isBannerContents = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  showCartWidget = true,
  secondaryButtonTrackingEvent,
  secondaryButtonTrackingEventKey,
  secondaryButtonTrackingEventValue,
}: Props) => {
  const navigationContext = useContext(NavigationV2Context)
  const numberOfItemsInCart = useGetCartQuantity()
  const [activeSubMenu, setActiveSubMenu] = useState<number | undefined>()
  const [scrolled, setScrolled] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const { pathname } = useLocation()
  const isDesktop = useIsBreakpoint("desktop")

  const { setNavigationMode } = usePageLayoutState()

  // We use this to ensure the menu doesnt flash at SSG step
  useLayoutEffect(() => {
    setIsMounted(true)
    setNavigationMode(mode)
  }, [])

  const mobileButtonText = mobilePrimaryButtonText || primaryButtonText
  const mobileButtonLink = mobilePrimaryButtonLink || primaryButtonLink
  const displayMobileButton = !!(mobileButtonText && mobileButtonLink)

  // Whenever route changes, close mobile menu
  useEffect(() => {
    if (navigationContext.isMobileMenuOpen) {
      navigationContext.setIsMobileMenuOpen(false)
      setActiveSubMenu(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleMainLinkHover = useCallback(
    (menuId: number) => {
      if (typeof menuId === "number" && isDesktop) {
        setActiveSubMenu(menuId)
      }
    },
    [isDesktop]
  )

  const handleMainLinkClick = useCallback((menuId: number) => {
    if (typeof menuId === "number") {
      setActiveSubMenu((prev) => (prev === menuId ? undefined : menuId))
    }
  }, [])

  const handleMobileMenuToggle = useCallback(
    (isOpen: boolean) => {
      navigationContext.setIsMobileMenuOpen(isOpen)
      if (!isOpen) {
        setActiveSubMenu(undefined)
      }
    },
    [navigationContext]
  )

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY
      const navElement = document.getElementById("navigation")
      if (!navElement) return

      const navTopPosition = navElement.getBoundingClientRect().top
      if (isBannerContents) {
        setScrolled(navTopPosition < 0)
      } else {
        setScrolled(offset > 1)
      }
    }

    // Set initial scroll state
    handleScroll()

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isBannerContents])

  const isMenuActive = activeSubMenu !== undefined

  const isMenuActiveOnDesktop = isMounted ? isMenuActive && isDesktop : false
  const isMenuOpenOnMobile = isMounted
    ? navigationContext.isMobileMenuOpen && !isDesktop
    : false
  const cartContext = useContext(CartContext)

  useEffect(() => {
    if (cartContext.cartPreviewOpen) {
      navigationContext.setIsMobileMenuOpen(false)
      setActiveSubMenu(undefined)
    }
  }, [cartContext.cartPreviewOpen, navigationContext])

  const color =
    mode === "light" || scrolled || isMenuActiveOnDesktop || isMenuOpenOnMobile
      ? Color.Black
      : Color.White

  return (
    <Section
      id="navigation"
      className={classNames(
        "pt-md pb-sm left-0 z-30 flex h-[68px] max-h-screen w-screen transition-colors duration-200 ease-in-out sm:z-20",
        isBackgroundTransparent
          ? ""
          : mode === "light"
            ? "bg-white"
            : "bg-black",
        {
          "bg-white": scrolled || isMenuActiveOnDesktop || isMenuOpenOnMobile,
        },
        { "fixed top-0": scrolled },
        {
          "shadow-[0_16px_16px_-8px] shadow-charcoal/20 lg:shadow-none":
            navigationContext.isMobileMenuOpen,
        },
        { absolute: !scrolled },
        {
          "!z-50": isMenuActiveOnDesktop || isMenuOpenOnMobile,
        }
      )}
      onMouseLeave={() => {
        setActiveSubMenu(undefined)
      }}
    >
      <div className="flex max-h-full flex-1 flex-col gap-x-32 lg:flex-row">
        {/* ─── LOGO + HAMBURGER ─--- */}
        <div className="mt-0 flex items-center justify-between sm:-mt-1">
          <Link
            link={`${process.env.GATSBY_PREFIX_PATH || ""}/`}
            aria-label="Yoco Logo"
          >
            <YocoLogoIcon
              className={classNames(
                mode === "light" ||
                  scrolled ||
                  isMenuActiveOnDesktop ||
                  isMenuOpenOnMobile
                  ? "fill-black"
                  : "fill-white"
              )}
            />
          </Link>

          <div className="flex items-center gap-x-24 md:gap-x-40 lg:hidden">
            {numberOfItemsInCart > 0 && showCartWidget ? (
              <CartWidget numberOfItems={numberOfItemsInCart} />
            ) : null}

            {!hideMobileMenu ? (
              <HamburgerIcon
                color={color}
                mobileMenuOpen={navigationContext.isMobileMenuOpen}
                onToggle={handleMobileMenuToggle}
              />
            ) : null}
          </div>
        </div>
        {/* ─── NAV LINKS ──── */}
        <div
          className={classNames(
            "pointer-events-none -mx-gutter-xs mt-8 flex max-h-0 flex-1 flex-col justify-between overflow-y-hidden text-body-md font-medium transition-all duration-200 ease-in-out sm:-mx-gutter-sm md:-mx-gutter-md lg:pointer-events-auto lg:-mx-24 lg:mt-0 lg:max-h-screen lg:flex-row lg:items-center lg:overflow-y-visible lg:bg-transparent lg:px-24",
            isMenuOpenOnMobile
              ? "pointer-events-auto min-h-[95svh]"
              : "translate-x-full lg:[transform:unset]"
          )}
        >
          {/* ─ DESKTOP: Always show main links inline, with hover popups ──────── */}
          {isMounted && isDesktop && (
            <div className="flex flex-1 lg:flex-row">
              {mainLinks &&
                mainLinks.map((mainLink, index) => (
                  <NavHeading
                    mainLink={mainLink}
                    key={`${mainLink.label}-${index}`}
                    className={MAIN_LINK_CLASSES}
                    mode={mode}
                    scrolled={scrolled}
                    isMenuActive={isMenuActive}
                    handleClick={() => handleMainLinkClick(index)}
                    handleHover={() => handleMainLinkHover(index)}
                    handleClose={() => setActiveSubMenu(undefined)}
                    isSubMenuActive={activeSubMenu === index}
                    isMenuOpenOnMobile={isMenuOpenOnMobile}
                  />
                ))}
            </div>
          )}

          {/* ─ MOBILE ── */}
          {isMounted && !isDesktop && isMenuOpenOnMobile && (
            <MobileNavPanels
              mainLinks={mainLinks || []}
              mode={mode}
              scrolled={scrolled}
              secondaryButtonText={secondaryButtonText}
              secondaryButtonLink={secondaryButtonLink}
            />
          )}

          {/* ─ DESKTOP RIGHT ITEMS -─ */}
          <div className="relative hidden items-center justify-between gap-x-8 py-[11px] sm:flex lg:py-0">
            <div className="hidden items-center lg:flex">
              <Link
                link="https://support.yoco.help/en/"
                className={classNames(
                  MAIN_LINK_CLASSES,
                  "group gap-x-10 border-b-transparent transition-opacity duration-200 ease-in-out",
                  {
                    "text-black lg:text-white":
                      mode === "dark" && !scrolled && !isMenuActive,
                  },
                  {
                    "text-black":
                      (mode === "light" && scrolled) || isMenuActive,
                  }
                )}
              >
                Support
              </Link>
              <Link
                className={classNames(
                  MAIN_LINK_CLASSES,
                  "border-b-transparent transition-opacity duration-200 ease-in-out",
                  {
                    "text-black lg:text-white":
                      mode === "dark" && !scrolled && !isMenuActive,
                  },
                  {
                    "text-black":
                      (mode === "light" && scrolled) || isMenuActive,
                  }
                )}
                onClick={(e) =>
                  handleOnClick({
                    e,
                    trackingEvent: secondaryButtonTrackingEvent,
                    trackingEventKey: secondaryButtonTrackingEventKey,
                    trackingEventValue: secondaryButtonTrackingEventValue,
                  })
                }
                link={secondaryButtonLink}
              >
                {secondaryButtonText}
              </Link>
              <div className="hidden transition-opacity duration-200 ease-in-out lg:block">
                {numberOfItemsInCart > 0 && showCartWidget ? (
                  <CartWidget numberOfItems={numberOfItemsInCart} />
                ) : (
                  !!(primaryButtonText && primaryButtonLink) && (
                    <PillButton
                      size="small"
                      hideIcon
                      color={Color.Blue}
                      overrideTextColor={color}
                      style="bordered"
                      text={primaryButtonText}
                      linkUrl={primaryButtonLink}
                      className="transition-all"
                      trackingEvent={trackingEvent}
                      trackingEventKey={trackingEventKey}
                      trackingEventValue={trackingEventValue}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile "CTA" Button at Bottom
       * Note that this is seperate to the StickyContainer component
       */}
      {displayMobileButton && (
        <div
          className={classNames(
            "fixed bottom-0 left-0 z-10 w-full border-t border-charcoal/10 bg-white px-20 py-[14px] transition-all duration-200 ease-in-out sm:hidden",
            navigationContext.isMobileMenuOpen
              ? "translate-y-0"
              : "translate-y-full"
          )}
        >
          <PillButton
            size="medium"
            color={Color.Blue}
            style="solid"
            text={mobileButtonText}
            linkUrl={mobileButtonLink}
            className="transition-opacity duration-200 ease-in-out"
          />
        </div>
      )}
    </Section>
  )
}

export default Navigation
